import React from 'react'
import { Link } from 'react-router-dom'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Tooltip from '@mui/material/Tooltip'
import { FulfillmentOrderStatus } from '../../graphql'
import { FulfillmentOrderRow } from './hooks/useFulfillmentOrdersDataQuery'
import { FO_STATUS_CHIP_COLOR, FO_STATUS_LABEL, FO_TYPE_LABEL } from './constants'
import { PICK_ORDER_PREFIX } from './pick-orders/constants'
import { formatDateTime } from '../../utils/formatters/date'
import getFulfillmentOrderSource from './getFulfillmentOrderSource'

function FulfillmentProgress( { row }: { row: FulfillmentOrderRow } ) {
	const total = row.lineItems.reduce( ( s, li ) => s + li.quantity, 0 )
	const fulfilled = row.lineItems.reduce( ( s, li ) => s + li.quantityFulfilled, 0 )
	const pct = total > 0 ? Math.round( ( fulfilled / total ) * 100 ) : 0

	return (
		<Tooltip title={`${fulfilled} / ${total} items fulfilled`}>
			<Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.75 }}>
				<LinearProgress
					variant="determinate"
					value={pct}
					sx={{ flex: 1, height: 6, borderRadius: 3 }}
					color={pct === 100 ? 'success' : 'primary'}
				/>
				<Box component="span" sx={{ fontSize: 11, whiteSpace: 'nowrap', color: 'text.secondary' }}>{fulfilled}/{total}</Box>
			</Box>
		</Tooltip>
	)
}

const columns: GridColDef[] = [
	{
		field: 'orderNumber',
		headerName: 'FO #',
		width: 100,
		renderCell: ( params: GridRenderCellParams ) => (
			<Link to={`/dc-admin/fulfillment-orders/${( params.row as FulfillmentOrderRow ).orderNumber}`}>
				FO-{params.value}
			</Link>
		),
	},
	{
		field: 'source',
		headerName: 'Source',
		width: 180,
		sortable: false,
		filterable: false,
		valueGetter: ( _value, row ) => getFulfillmentOrderSource( row as FulfillmentOrderRow ) ?? '—',
	},
	{
		field: 'status',
		headerName: 'Status',
		width: 190,
		renderCell: ( params: GridRenderCellParams ) => {
			const status = params.value as FulfillmentOrderStatus
			if ( !status ) return null
			const row = params.row as FulfillmentOrderRow
			// Being picked is not a status of its own — the order is still submitted
			// for fulfillment — so it sits under the chip rather than replacing it.
			// Only open runs come back, so this is what is happening now.
			const [ run ] = row.pickOrders ?? []
			return (
				<Box sx={{ py: 0.5 }}>
					<Chip
						label={FO_STATUS_LABEL[ status ] ?? status}
						color={FO_STATUS_CHIP_COLOR[ status ] ?? 'default'}
					/>
					{run && (
						<Box sx={{ mt: 0.25 }}>
							<Link
								to={`/dc-admin/pick-orders/${run.id}`}
								onClick={( event ) => event.stopPropagation()}
							>
								Picking on {PICK_ORDER_PREFIX}{run.pickOrderNumber}
							</Link>
						</Box>
					)}
				</Box>
			)
		},
	},
	{
		field: 'fulfillmentType',
		headerName: 'Type',
		width: 160,
		valueFormatter: ( value ) => FO_TYPE_LABEL[ value as keyof typeof FO_TYPE_LABEL ] ?? value,
	},
	{
		field: 'destinationInventory',
		headerName: 'Destination',
		width: 180,
		valueGetter: ( _value, row ) => ( row as FulfillmentOrderRow ).destinationInventory?.name ?? '—',
	},
	{
		field: 'mttId',
		headerName: 'MTT ID',
		width: 100,
		valueFormatter: ( value ) => value ?? '—',
	},
	{
		field: 'lineItems',
		headerName: 'Fulfillment',
		width: 160,
		sortable: false,
		filterable: false,
		renderCell: ( params: GridRenderCellParams ) => (
			<FulfillmentProgress row={params.row as FulfillmentOrderRow} />
		),
	},
	{
		field: 'dateCreated',
		headerName: 'Created',
		width: 155,
		valueFormatter: ( value ) => formatDateTime( value ),
	},
	{
		field: 'dateSubmittedForFulfillment',
		headerName: 'Submitted',
		width: 155,
		valueFormatter: ( value ) => formatDateTime( value ),
	},
	{
		field: 'dateCompleted',
		headerName: 'Completed',
		width: 155,
		valueFormatter: ( value ) => formatDateTime( value ),
	},
]

export default columns
